import { useEffect } from 'react'
import { useSearchParams } from 'next/navigation'
import { useRecoilState } from 'recoil'
import {
  homeSelectedTagIdsState,
  homeSortingState,
  clickedTagsWithBadgeState,
} from 'Store/home'
import { parseTagsFromUrl } from '@/utils/home/parseUrlParams'
import { TagsSFW, TagsNSFW, ensureTagsLoaded } from '@/constant/tags'

/**
 * 从 URL 参数初始化首页筛选条件的自定义 Hook
 * 支持 series 和 tags 参数
 *
 * @param options.nsfw - 当前是否为 NSFW 模式
 * @param options.sortingList - 允许的排序列表（桌面端用 SortingList，移动端用 MobileSortingList）
 * @param options.onFiltersApplied - 可选回调，当筛选条件被应用后触发（移动端用于打开 filter drawer）
 */
export function useUrlFilterParams(options: {
  nsfw: boolean
  sortingList: readonly string[]
  onFiltersApplied?: (params: { series?: string; tags?: string }) => void
}) {
  const { nsfw, sortingList, onFiltersApplied } = options
  const searchParams = useSearchParams()
  const [, setSorting] = useRecoilState(homeSortingState)
  const [, setSelectedTagIds] = useRecoilState(homeSelectedTagIdsState)
  const [clickedTagsWithBadge, setClickedTagsWithBadge] = useRecoilState(
    clickedTagsWithBadgeState
  )

  useEffect(() => {
    const applyFilters = async () => {
      const seriesParam = searchParams.get('series')
      const tagsParam = searchParams.get('tags')

      if (!seriesParam && !tagsParam) return

      // 等待标签数据加载完成
      await ensureTagsLoaded()

      let appliedSeries: string | undefined
      let appliedTags: string | undefined

      // 处理 series 参数
      if (seriesParam && sortingList.includes(seriesParam as any)) {
        setSorting(seriesParam as any)
        localStorage.setItem('homeSortingState', seriesParam)
        appliedSeries = seriesParam
      }

      // 处理 tags 参数
      if (tagsParam) {
        const filterTags = nsfw ? TagsNSFW : TagsSFW
        const tagIds = parseTagsFromUrl(tagsParam, filterTags)

        if (tagIds.length > 0) {
          setSelectedTagIds(tagIds)
          appliedTags = tagsParam

          // 检查是否有红点tag被选中，标记为已点击
          const tagNameArray = tagsParam
            .split(',')
            .map((name) => name.trim())
            .filter((name) => name.length > 0)
          const newClickedTags = tagNameArray.filter(
            (tagName) =>
              tagNameArray.includes(tagName) &&
              !clickedTagsWithBadge.includes(tagName)
          )
          if (newClickedTags.length > 0) {
            setClickedTagsWithBadge([
              ...clickedTagsWithBadge,
              ...newClickedTags,
            ])
          }
        }
      }

      // 如果有筛选条件被应用，触发回调
      if ((appliedSeries || appliedTags) && onFiltersApplied) {
        onFiltersApplied({ series: appliedSeries, tags: appliedTags })
      }
    }

    applyFilters()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, nsfw])
}
